import { createElement } from "react";

import type { SagaIterator } from "redux-saga";
import { call, put, select, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

// notifications

import * as rfqApi from "@/shop/api/shopping/rfq";
import RfqErrorNotice from "@/shop/components/Cart/Messages/RfqErrorNotice";
import { displayErrorMessage, displayWarningMessage } from "@/shop/components/common/Messages";
import type { IShoppingCartPosition } from "@/shop/types/shopping/cart";
import type { IRfqStartResponse, IRfqTransaction } from "@/shop/types/shopping/rfq";

import * as RfqActions from "./actions";
import { AddAttachmentFailNotice, RemoveAttachmentFailNotice } from "./notices";
import * as rfqListActions from "./rfq-list/actions";
import { getRfq, getShowRfqCommissionsPage } from "./selectors";
import * as cartActions from "../cart/actions";
import { getEnabledOrderPositions, getEnabledRequestPositions } from "../cart/selectors";

function* startRfq(action: ActionType<typeof RfqActions.startRfq>): SagaIterator {
  try {
    const { navigate, includeOrderPositions } = action.payload;

    let positions = (yield select(getEnabledRequestPositions)) as IShoppingCartPosition[];

    if (includeOrderPositions) {
      const orderPositions = (yield select(getEnabledOrderPositions)) as IShoppingCartPosition[];
      // set new sequence numbers to avoid collisions
      positions = [...orderPositions, ...positions].map((pos, index) => ({ ...pos, sequenceNumber: (index + 1) * 10 }));
    }

    const response = (yield call(rfqApi.startRfq, { positions })) as IRfqStartResponse;

    const showRfqCommissionsPage = (yield select(getShowRfqCommissionsPage)) as boolean;

    if (showRfqCommissionsPage && response.ids.length > 1) {
      // multiple responses, jump to list page
      void navigate(`/cart/rfq/list`);
      // trigger fetch of rfq list TODO: or rather do in component?
      yield put(rfqListActions.fetchRfqList());
    } else if (response.ids.length >= 1) {
      // single rfq: jump to rfq immediately
      void navigate(`/cart/rfq/edit/${encodeURIComponent(response.ids[0]!)}`);
    } else {
      throw new Error("This should not happen ... but we will get a nice orange error box in this case");
    }
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(RfqActions.startRfqFail(err));
    displayWarningMessage(createElement(RfqErrorNotice));
  }
}

function* fetchRfq(action: ActionType<typeof RfqActions.fetchRfq>): SagaIterator {
  const id = action.payload;
  try {
    yield put(RfqActions.fetchRfqStart(id));
    const rfq = (yield call(rfqApi.fetchRfq, id)) as IRfqTransaction;
    yield put(RfqActions.fetchRfqSuccess(rfq));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(RfqActions.fetchRfqFail(err));
  }
}

function* fetchRfqList(_: ActionType<typeof rfqListActions.fetchRfqList>): SagaIterator {
  try {
    yield put(rfqListActions.fetchRfqListStart());
    const rfqList = (yield call(rfqApi.fetchRequestForQuotationList)) as IRfqTransaction[];
    yield put(rfqListActions.fetchRfqListSuccess(rfqList));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(rfqListActions.fetchRfqListFail(err));
  }
}

function* removeAllRfqListItems(_: ActionType<typeof rfqListActions.removeAllRfqListItems>): SagaIterator {
  try {
    // remove all items
    yield call(rfqApi.deleteRequestForQuotationList);
    // update list, even though it should be empty
    yield put(rfqListActions.fetchRfqList());
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(rfqListActions.fetchRfqListFail(err));
  }
}

function* removeRfqListItem(action: ActionType<typeof rfqListActions.removeRfqListItem>): SagaIterator {
  try {
    // delete item
    yield call(rfqApi.deleteRequestForQuotationListItem, action.payload);
    // and update list
    yield put(rfqListActions.fetchRfqList());
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(rfqListActions.fetchRfqListFail(err));
  }
}

function* addRfqAttachment(action: ActionType<typeof RfqActions.addRfqAttachment>): SagaIterator {
  try {
    const { posId, file } = action.payload;
    const rfq = (yield select(getRfq)) as IRfqTransaction;
    const posIndex = rfq.positions.findIndex((pos) => pos.id === posId);
    yield call(rfqApi.addRfqAttachment, rfq.id, posIndex, file);
    yield put(RfqActions.fetchRfq(rfq.id));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (Object.hasOwn(err, "statusCode")) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      displayErrorMessage(createElement(AddAttachmentFailNotice, { error: err }));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      displayErrorMessage(createElement(AddAttachmentFailNotice, { message: err.message }));
    }
  }
}

function* removeRfqAttachment(action: ActionType<typeof RfqActions.removeRfqAttachment>): SagaIterator {
  try {
    const { posId } = action.payload;
    const rfq = (yield select(getRfq)) as IRfqTransaction;
    const posIndex = rfq.positions.findIndex((pos) => pos.id === posId);
    yield call(rfqApi.removeRfqAttachment, rfq.id, posIndex);
    yield put(RfqActions.fetchRfq(rfq.id));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (Object.hasOwn(err, "statusCode")) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      displayErrorMessage(createElement(RemoveAttachmentFailNotice, { error: err }));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      displayErrorMessage(createElement(RemoveAttachmentFailNotice, { message: err.message }));
    }
  }
}

function* updateRfq(action: ActionType<typeof RfqActions.updateRfq>): SagaIterator {
  try {
    const rfq = (yield select(getRfq)) as IRfqTransaction;
    const nextRfq = (yield call(rfqApi.updateRfq, rfq.id, action.payload)) as IRfqTransaction;
    yield put(RfqActions.fetchRfqSuccess(nextRfq));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(RfqActions.fetchRfqFail(err));
  }
}

function* submitRfq(action: ActionType<typeof RfqActions.submitRfq>): SagaIterator {
  try {
    const rfq = (yield select(getRfq)) as IRfqTransaction;
    // throws on error
    yield call(rfqApi.submitRfq, rfq.id, action.payload);
    // this should be one less entry now
    yield put(rfqListActions.fetchRfqList());
    // fetch the rfq again, this should contain the rfqNo
    yield put(RfqActions.fetchRfq(rfq.id));
    // TODO: this was used before to indicate success, can it be removed?
    yield put(RfqActions.submitRfqSuccess());
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(RfqActions.submitRfqFail(err));
  } finally {
    // FIXME: find out how cart is refreshed after rfq
    yield put(cartActions.fetch());
  }
}

export default function* rfqSaga(): SagaIterator {
  // single rfq
  yield takeLeading(getType(RfqActions.startRfq), startRfq);
  yield takeLeading(getType(RfqActions.fetchRfq), fetchRfq);
  yield takeLeading(getType(RfqActions.addRfqAttachment), addRfqAttachment);
  yield takeLeading(getType(RfqActions.removeRfqAttachment), removeRfqAttachment);
  yield takeLeading(getType(RfqActions.submitRfq), submitRfq);
  yield takeLeading(getType(RfqActions.updateRfq), updateRfq);
  // rfq list
  yield takeLeading(getType(rfqListActions.fetchRfqList), fetchRfqList);
  yield takeLeading(getType(rfqListActions.removeRfqListItem), removeRfqListItem);
  yield takeLeading(getType(rfqListActions.removeAllRfqListItems), removeAllRfqListItems);
}
